<template>
    <div class="prod-panel prod-panel--vgt">
        <div class="prod-header">
            <div class="prod-header-left">
                <div v-if="selectedProduct && (changedRowCount || checkedRows.length)" class="prod-header-badges">
                    <span v-if="changedRowCount" class="prod-state-chip prod-state-chip--warn">
                        미저장 {{ changedRowCount }}건
                    </span>
                    <span v-if="checkedRows.length" class="prod-state-chip">{{ checkedRows.length }} 선택</span>
                </div>
            </div>

            <div class="prod-header-actions">
                <button
                    type="button"
                    class="pa-btn pa-btn--primary"
                    :disabled="!selectedProduct || !hasLinkedProductionPoint"
                    @click="addRow"
                >
                    <i class="fas fa-plus"></i>
                    생산량 추가
                </button>
                <button
                    type="button"
                    class="pa-btn pa-btn--primary"
                    :disabled="!selectedProduct || !hasLinkedProductionPoint"
                    @click="saveAll"
                >
                    <i class="fas fa-check"></i>
                    저장
                </button>
                <button
                    type="button"
                    class="pa-btn pa-btn--danger"
                    :disabled="!checkedRows.length"
                    @click="deleteChecked"
                >
                    <i class="fas fa-trash"></i>
                    삭제
                </button>
            </div>
        </div>

        <div v-if="!selectedProduct" class="prod-guide">
            <div class="prod-guide-card">
                <i class="fas fa-arrow-left"></i>
                <strong>상품을 먼저 선택해 주세요</strong>
            </div>
        </div>

        <div v-else-if="!hasLinkedProductionPoint" class="prod-guide">
            <div class="prod-guide-card">
                <i class="fas fa-link"></i>
                <strong>생산 관제점을 연결해 주세요</strong>
                <span class="prod-guide-hint"
                    >상세 패널에서 기본 생산 관제점을 선택하면 생산량을 조회·저장할 수 있습니다.</span
                >
            </div>
        </div>

        <div v-else class="prod-table-area">
            <div class="prod-table-wrap">
                <vue-good-table
                    :columns="tableColumns"
                    :rows="filteredRows"
                    :fixed-header="true"
                    compactMode
                    :select-options="{
                        enabled: true,
                        disableSelectInfo: true,
                        selectOnCheckboxOnly: true,
                    }"
                    :sort-options="{ enabled: true }"
                    :row-style-class="rowClass"
                    @on-cell-click="handleCellClick"
                    @on-selected-rows-change="handleSelectedRowsChange"
                >
                    <div slot="emptystate" class="tbl-empty">
                        <i class="fas fa-inbox"></i>
                        <p>{{ emptyStateTitle }}</p>
                    </div>

                    <template slot="table-row" slot-scope="props">
                        <template v-if="props.column.field === 'production_date'">
                            <date-picker
                                v-if="props.row._editing"
                                v-model="props.row.production_date"
                                type="date"
                                format="YYYY-MM-DD"
                                value-type="format"
                                placeholder="날짜"
                                :input-class="'cell-date-inp'"
                                @change="onCellValueChange(props.row, 'production_date', $event)"
                            />
                            <div v-else class="cell-date">
                                <span class="cell-date-txt">{{ props.row.production_date }}</span>
                            </div>
                        </template>

                        <template v-else-if="props.column.field === 'production_qty'">
                            <div class="cell-qty" :class="{ 'cell-qty--editing': props.row._editing }">
                                <input
                                    v-if="props.row._editing"
                                    v-model.number="props.row.production_qty"
                                    type="number"
                                    step="1"
                                    min="0"
                                    class="cell-qty-inp"
                                    :data-production-id="props.row.production_id"
                                    :data-field="'production_qty'"
                                    @change="onCellValueChange(props.row, 'production_qty', $event.target.value)"
                                    @keydown.enter.prevent="commitRow(props.row)"
                                    @focus="$event.target.select()"
                                    @click.stop
                                />
                                <template v-else>
                                    <span class="cell-qty-val">{{ formatQty(props.row.production_qty) }}</span>
                                    <span class="cell-qty-u">{{
                                        selectedProduct && selectedProduct.product_unit
                                    }}</span>
                                </template>
                            </div>
                        </template>

                        <template v-else-if="props.column.field === 'defect_qty'">
                            <div class="cell-qty" :class="{ 'cell-qty--editing': props.row._editing }">
                                <input
                                    v-if="props.row._editing"
                                    v-model.number="props.row.defect_qty"
                                    type="number"
                                    step="1"
                                    min="0"
                                    class="cell-qty-inp"
                                    :data-production-id="props.row.production_id"
                                    :data-field="'defect_qty'"
                                    @change="onCellValueChange(props.row, 'defect_qty', $event.target.value)"
                                    @keydown.enter.prevent="commitRow(props.row)"
                                    @focus="$event.target.select()"
                                    @click.stop
                                />
                                <template v-else>
                                    <span class="cell-qty-val">{{ formatQty(props.row.defect_qty) }}</span>
                                </template>
                            </div>
                        </template>

                        <template v-else-if="props.column.field === '_act'">
                            <div class="cell-acts">
                                <button
                                    type="button"
                                    class="act-btn act-del"
                                    title="삭제"
                                    @click.stop="deleteRow(props.row)"
                                >
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </template>

                        <template v-else>
                            {{ props.formattedRow[props.column.field] }}
                        </template>
                    </template>
                </vue-good-table>
            </div>

            <div class="prod-table-footer">
                <span class="prod-product-tag prod-table-footer-product">
                    <i class="fas fa-cube"></i>
                    {{ selectedProduct.product_name }}
                    <em>{{ selectedProduct.product_unit }}</em>
                </span>
                <div class="prod-table-footer-chips">
                    <span class="prod-table-chip">{{ rangeSummary }}</span>
                    <span v-if="changedRowCount" class="prod-table-chip prod-table-chip--warn">
                        저장 필요 {{ changedRowCount }}건
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import backEndApi from "@src/api/backEndApi";
import {
    mapDailyApiToUiRow,
    regDateToUi,
    rowMatchesProductFilter,
    uiDayToRegDateInt,
} from "../config/productionMapper.js";

export default {
    name: "ProductionDataPanel",
    components: { DatePicker },
    props: {
        selectedProduct: { type: Object, default: null },
        sharedDateStart: { type: String, default: null },
        sharedDateEnd: { type: String, default: null },
        /** 부모(ProductionUnitConfigMain)가 getProductProduction 1회 조회로 넘기는 원시 rows */
        apiProductionRows: { type: Array, default: () => [] },
    },
    data() {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const fmt = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        return {
            dateStart: fmt(monthStart),
            dateEnd: fmt(today),
            allRows: [],
            checkedRows: [],
        };
    },
    computed: {
        productRows() {
            if (!this.selectedProduct) return [];
            return this.allRows.filter(
                (row) => row.product_id === this.selectedProduct.product_id && row._granularity === "DAY",
            );
        },
        changedRowCount() {
            return this.productRows.filter((row) => row._isNew || this.hasPersistedValueChange(row)).length;
        },
        tableColumns() {
            const dateLabel = "기준일";
            const qtyLabel = this.selectedProduct ? `생산량 (${this.selectedProduct.product_unit})` : "생산량";

            return [
                {
                    label: dateLabel,
                    field: "production_date",
                    width: "125px",
                    thClass: "vth text-center fixed-width-125",
                    tdClass: "vtd fixed-width-125 vtd-date-col",
                },
                {
                    label: qtyLabel,
                    field: "production_qty",
                    type: "number",
                    width: "160px",
                    thClass: "vth text-center fixed-width-qty-main",
                    tdClass: "vtd text-right vtd-qty-wrap fixed-width-qty-main",
                },
                {
                    label: "불량",
                    field: "defect_qty",
                    type: "number",
                    width: "120px",
                    thClass: "vth text-center fixed-width-qty",
                    tdClass: "vtd text-right vtd-qty-wrap fixed-width-qty",
                },
                {
                    label: "",
                    field: "_act",
                    sortable: false,
                    width: "56px",
                    thClass: "vth text-center fixed-width-act",
                    tdClass: "vtd text-center fixed-width-act",
                },
            ];
        },
        filteredRows() {
            if (!this.selectedProduct) return [];

            return this.allRows.filter((row) => {
                if (row.product_id !== this.selectedProduct.product_id) return false;
                if (row._granularity !== "DAY") return false;

                if (this.dateStart && row.production_date < this.dateStart) return false;
                if (this.dateEnd && row.production_date > this.dateEnd) return false;
                return true;
            });
        },
        hasChanges() {
            return this.productRows.some((row) => row._isNew || this.hasPersistedValueChange(row));
        },

        rangeSummary() {
            return `${this.dateStart || "-"} ~ ${this.dateEnd || "-"}`;
        },
        emptyStateTitle() {
            return "해당 기간에 데이터가 없습니다";
        },
        hasLinkedProductionPoint() {
            const p = this.selectedProduct;
            if (!p) return false;
            return Boolean(p.pt_addr || (p.default_prod_pt_idx != null && p.default_prod_pt_idx !== ""));
        },
    },
    watch: {
        selectedProduct: {
            handler(product) {
                this.checkedRows = [];
                this.allRows = this.allRows.filter((r) => r._granularity !== "MONTH");
                if (!product) return;
                this.applyDefaultPeriod();
                this.$nextTick(() => this.mergeProductionRowsFromApi());
            },
            immediate: true,
        },
        apiProductionRows: {
            handler() {
                this.mergeProductionRowsFromApi();
            },
        },
        sharedDateStart: {
            handler(val) {
                if (val) this.dateStart = val;
            },
            immediate: true,
        },
        sharedDateEnd: {
            handler(val) {
                if (val) this.dateEnd = val;
            },
            immediate: true,
        },
    },
    methods: {
        /** axios 응답 또는 에러에서 서버 메시지(detail 우선) 추출 */
        pickSaveErrorMessage(resOrAxiosError) {
            const res =
                resOrAxiosError && resOrAxiosError.response != null ? resOrAxiosError.response : resOrAxiosError;
            if (!res || res.data == null) return "";
            const data = res.data;
            if (typeof data === "string") return data.trim();
            if (typeof data === "object") {
                const d = data.detail != null ? String(data.detail).trim() : "";
                if (d) return d;
                const m = data.message != null ? String(data.message).trim() : "";
                if (m) return m;
            }
            return "";
        },
        isEditableField(field) {
            return field === "production_date" || field === "production_qty" || field === "defect_qty";
        },
        normalizeNumber(value, { allowNull = false, fallback = 0 } = {}) {
            if (value === "" || value == null) return allowNull ? null : fallback;
            const parsed = Number(value);
            if (!Number.isFinite(parsed)) return allowNull ? null : fallback;
            return parsed;
        },
        normalizeFieldValue(field, rawValue) {
            if (field === "production_qty") {
                return this.normalizeNumber(rawValue, { allowNull: false, fallback: 0 });
            }

            if (field === "defect_qty") {
                return this.normalizeNumber(rawValue, { allowNull: true, fallback: null });
            }

            if (field === "production_date") {
                return rawValue == null || rawValue === "" ? null : String(rawValue);
            }

            return rawValue;
        },
        hasPersistedValueChange(row) {
            if (!row) return false;
            const curQty = this.normalizeNumber(row.production_qty, { allowNull: false, fallback: 0 });
            const curDefect = this.normalizeNumber(row.defect_qty, { allowNull: true, fallback: null });

            return row.production_date !== row._origDate || curQty !== row._origQty || curDefect !== row._origDefectQty;
        },
        fmt(date) {
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        },
        formatQty(value) {
            const number = Number(value || 0);
            return Number.isInteger(number)
                ? number.toLocaleString()
                : number.toLocaleString(undefined, { maximumFractionDigits: 3 });
        },
        applyDefaultPeriod() {
            if (!this.selectedProduct) return;
            if (this.sharedDateStart && this.sharedDateEnd) {
                this.dateStart = this.sharedDateStart;
                this.dateEnd = this.sharedDateEnd;
                return;
            }
            const today = new Date();
            this.dateStart = this.fmt(new Date(today.getFullYear(), today.getMonth(), 1));
            this.dateEnd = this.fmt(today);
        },
        /** 부모가 넘긴 apiProductionRows를 현재 상품·기간에 맞게 테이블에 반영 (getProductProduction은 부모만 호출) */
        mergeProductionRowsFromApi() {
            const p = this.selectedProduct;
            if (!p) return;
            if (!p.pt_addr && (p.default_prod_pt_idx == null || p.default_prod_pt_idx === "")) {
                this.allRows = this.allRows.filter((r) => r.product_id !== p.product_id);
                return;
            }

            const pid = p.product_id;
            const code = p.product_code != null ? String(p.product_code).trim() : "";
            const rows = Array.isArray(this.apiProductionRows) ? this.apiProductionRows : [];
            const collected = [];
            for (let j = 0; j < rows.length; j += 1) {
                const apiRow = rows[j];
                if (!rowMatchesProductFilter(apiRow, code)) continue;
                const dateUi = regDateToUi(apiRow.regDate);
                if (this.dateStart && dateUi < this.dateStart) continue;
                if (this.dateEnd && dateUi > this.dateEnd) continue;
                collected.push(mapDailyApiToUiRow(apiRow, p));
            }

            this.allRows = this.allRows.filter((r) => r.product_id !== pid);
            this.allRows.push(...collected);
            this.checkedRows = [];
        },
        reload() {
            this.$emit("request-production-refresh");
        },
        syncCheckedRowsById(selectedRowsLike = this.checkedRows) {
            const ids = new Set((selectedRowsLike || []).map((row) => row.production_id));
            this.checkedRows = this.allRows.filter((row) => ids.has(row.production_id));
        },
        getSourceRow(rowLike) {
            if (!rowLike) return null;
            return this.allRows.find((row) => row.production_id === rowLike.production_id) || null;
        },
        syncDraftFieldToSource(rowLike, field, rawValue) {
            if (!this.isEditableField(field)) return null;
            const src = this.getSourceRow(rowLike);
            if (!src) return null;

            const normalized = this.normalizeFieldValue(field, rawValue);
            this.$set(src, field, normalized);
            if (rowLike && rowLike !== src) this.$set(rowLike, field, normalized);

            return src;
        },
        syncDraftRowToSource(rowLike) {
            if (!rowLike) return null;
            const src = this.getSourceRow(rowLike);
            if (!src) return rowLike;

            const editableFields = ["production_date", "production_qty", "defect_qty"];
            editableFields.forEach((field) => {
                const normalized = this.normalizeFieldValue(field, rowLike[field]);
                this.$set(src, field, normalized);
                if (rowLike !== src) this.$set(rowLike, field, normalized);
            });

            return src;
        },
        handleCellClick({ row, column }) {
            if (!column || !this.isEditableField(column.field)) return;
            if (!row._editing) this.startEdit(row);

            if (column.field === "production_date") return;

            this.$nextTick(() => {
                const selector = `.cell-qty-inp[data-production-id="${row.production_id}"][data-field="${column.field}"]`;
                const input = this.$el && this.$el.querySelector ? this.$el.querySelector(selector) : null;
                if (input) input.focus();
            });
        },
        onCellValueChange(rowLike, field, rawValue) {
            const src = this.syncDraftFieldToSource(rowLike, field, rawValue);
            if (!src) return;

            src._editing = true;
            if (rowLike) rowLike._editing = true;

            if (!src._isNew) {
                src._changed = this.hasPersistedValueChange(src);
                if (rowLike && rowLike !== src) rowLike._changed = src._changed;
            }

            this.$emit("cell-value-changed", {
                row: src,
                field,
                value: src[field],
            });
        },
        handleSelectedRowsChange({ selectedRows }) {
            this.syncCheckedRowsById(selectedRows);
        },
        defaultPeriodForProduct() {
            const today = new Date();
            return {
                current: this.fmt(today),
                origin: this.fmt(today),
            };
        },
        addRow() {
            if (!this.selectedProduct) return;

            const { current, origin } = this.defaultPeriodForProduct();
            this.allRows.unshift({
                production_id: `new-${Date.now()}`,
                product_id: this.selectedProduct.product_id,
                production_date: current,
                production_qty: 0,
                defect_qty: null,
                _productCode:
                    this.selectedProduct && this.selectedProduct.product_code
                        ? String(this.selectedProduct.product_code).trim()
                        : "",
                _granularity: "DAY",
                _editing: true,
                _changed: false,
                _isNew: true,
                _origDate: origin,
                _origQty: 0,
                _origDefectQty: null,
            });
        },
        startEdit(row) {
            if (!row) return;
            row._editing = true;
            const src = this.getSourceRow(row);
            if (src && src !== row) src._editing = true;
        },
        commitRow(rowLike) {
            if (!rowLike) return true;

            const src = this.syncDraftRowToSource(rowLike);
            const target = src || rowLike;
            if (!target._editing && !rowLike._editing) return true;

            target._editing = false;
            rowLike._editing = false;

            if (!target._isNew) {
                target._changed = this.hasPersistedValueChange(target);
                if (rowLike !== target) rowLike._changed = target._changed;
            }

            this.syncCheckedRowsById();
            this.$forceUpdate();
            return true;
        },
        async deleteRow(row) {
            const result = await this.alertConfirmWarning(`${row.production_date} 데이터를 삭제하시겠습니까?`);
            if (!result.value) return;

            if (!row._isNew) {
                const p = this.selectedProduct;
                if (!p || (!p.pt_addr && (p.default_prod_pt_idx == null || p.default_prod_pt_idx === ""))) {
                    this.alertNoti("관제점이 연결된 상품만 삭제할 수 있습니다.");
                    return;
                }
                try {
                    const regDate = uiDayToRegDateInt(row.production_date);
                    const params = { regDate };
                    if (p.default_prod_pt_idx != null && p.default_prod_pt_idx !== "") {
                        params.ptIdx = p.default_prod_pt_idx;
                    } else {
                        params.ptAddr = p.pt_addr;
                    }
                    const res = await backEndApi.peiProduction.deleteProductProduction(params);
                    if (res.status !== 200) throw new Error("api");
                } catch {
                    this.alertNoti("삭제에 실패했습니다.");
                    return;
                }
            }

            const rowIndex = this.allRows.findIndex((targetRow) => targetRow.production_id === row.production_id);
            if (rowIndex !== -1) this.allRows.splice(rowIndex, 1);
            this.$forceUpdate();
        },
        async deleteChecked() {
            if (!this.checkedRows.length) return;

            const result = await this.alertConfirmWarning(`선택한 ${this.checkedRows.length}건을 삭제하시겠습니까?`);
            if (!result.value) return;

            const p = this.selectedProduct;
            if (!p || (!p.pt_addr && (p.default_prod_pt_idx == null || p.default_prod_pt_idx === ""))) {
                this.alertNoti("관제점이 연결된 상품만 삭제할 수 있습니다.");
                return;
            }

            try {
                for (let i = 0; i < this.checkedRows.length; i += 1) {
                    const row = this.checkedRows[i];
                    if (row._isNew) continue;
                    const regDate = uiDayToRegDateInt(row.production_date);
                    const params = { regDate };
                    if (p.default_prod_pt_idx != null && p.default_prod_pt_idx !== "") {
                        params.ptIdx = p.default_prod_pt_idx;
                    } else {
                        params.ptAddr = p.pt_addr;
                    }
                    const res = await backEndApi.peiProduction.deleteProductProduction(params);
                    if (res.status !== 200) throw new Error("api");
                }
            } catch {
                this.alertNoti("삭제에 실패했습니다.");
                return;
            }

            const ids = new Set(this.checkedRows.map((r) => r.production_id));
            this.allRows = this.allRows.filter((r) => !ids.has(r.production_id));
            this.checkedRows = [];
            this.$forceUpdate();
        },
        async saveAll() {
            // 조회 기간 필터 때문에 화면에서 잠깐 사라진 편집행도 저장 대상에 포함해야 한다.
            const targetRows = this.productRows;
            const editingRows = targetRows.filter((row) => row._editing);
            for (let index = 0; index < editingRows.length; index += 1) {
                if (!this.commitRow(editingRows[index])) return;
            }

            const dirtyRows = targetRows.filter((row) => row._isNew || this.hasPersistedValueChange(row));
            if (!dirtyRows.length) {
                this.alertNoti("저장할 변경 내용이 없습니다.");
                return;
            }

            const p = this.selectedProduct || {};

            const dailyPayload = [];
            for (let i = 0; i < dirtyRows.length; i += 1) {
                const row = dirtyRows[i];
                const rowProductCode = row && row._productCode != null ? String(row._productCode).trim() : "";
                const fallbackCode = p && p.product_code != null ? String(p.product_code).trim() : "";
                const productCode = rowProductCode || fallbackCode || null;
                const common = {
                    productCode,
                    quantity: row.production_qty,
                    defectQty: row.defect_qty,
                };
                if (p.default_prod_pt_idx != null && p.default_prod_pt_idx !== "") {
                    common.ptIdx = p.default_prod_pt_idx;
                } else if (p.pt_addr) {
                    common.ptAddr = p.pt_addr;
                }

                dailyPayload.push({
                    ...common,
                    regDate: uiDayToRegDateInt(row.production_date),
                });
            }

            try {
                if (dailyPayload.length) {
                    const res = await backEndApi.peiProduction.insertProductProduction({ rows: dailyPayload });
                    if (res.status !== 200) {
                        const msg = this.pickSaveErrorMessage(res);
                        this.alertNoti(msg || "저장에 실패했습니다.");
                        return;
                    }
                }
            } catch (err) {
                const msg = this.pickSaveErrorMessage(err);
                this.alertNoti(msg || "저장에 실패했습니다.");
                return;
            }

            dirtyRows.forEach((row) => {
                row._changed = false;
                row._isNew = false;
                row._origDate = row.production_date;
                row._origQty = row.production_qty;
                row._origDefectQty = row.defect_qty;
                row._editing = false;
            });

            await this.alertNoti(`${dirtyRows.length}건 저장 완료`);
            this.$emit("request-production-refresh");
            this.$forceUpdate();
        },
        rowClass(row) {
            const classes = [];
            if (row._isNew) classes.push("row-new");
            if (!row._isNew && this.hasPersistedValueChange(row)) classes.push("row-mod");
            if (row._editing) classes.push("row-editing");
            return classes.join(" ");
        },
    },
};
</script>

<style scoped>
.prod-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.7), rgba(255, 255, 255, 0.98));
}

.prod-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 10px 8px 10px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.96);
    background: linear-gradient(180deg, rgba(241, 245, 249, 0.96), rgba(255, 255, 255, 0.92));
    flex-shrink: 0;
}

.prod-header-left {
    display: flex;
    gap: 6px;
    min-width: 0;
}

.prod-title-wrap {
    display: flex;
    flex-direction: column;
}

.prod-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
}

.prod-header-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.prod-product-tag,
.prod-state-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 22px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.prod-product-tag {
    background: rgba(241, 245, 249, 0.96);
    color: var(--puc-slate-700, #475569);
    border: 1px solid rgba(226, 232, 240, 0.96);
}

.prod-product-tag i {
    color: var(--puc-slate-400, #94a3b8);
}

.prod-product-tag em {
    font-style: normal;
    color: var(--puc-slate-400, #94a3b8);
}

.prod-state-chip {
    background: rgba(248, 250, 252, 0.96);
    color: var(--puc-slate-500, #64748b);
    border: 1px solid rgba(226, 232, 240, 0.96);
}

.prod-state-chip--warn {
    background: rgba(245, 158, 11, 0.1);
    color: var(--puc-amber, #f59e0b);
    border-color: rgba(245, 158, 11, 0.2);
}

.prod-header-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex-shrink: 0;
}

.pa-btn {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 32px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.96);
    background: rgba(255, 255, 255, 0.96);
    color: var(--puc-slate-700, #475569);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 0.18s ease,
        border-color 0.18s ease,
        color 0.18s ease,
        transform 0.18s ease;
    white-space: nowrap;
}

.pa-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.pa-btn:not(:disabled):hover {
    transform: translateY(-1px);
}

.pa-btn--primary:not(:disabled):hover {
    border-color: rgba(37, 99, 235, 0.3);
    background: rgba(0, 123, 255, 0.08);
    color: var(--puc-blue-strong, #2563eb);
}

.pa-btn--danger:not(:disabled):hover {
    border-color: rgba(244, 63, 94, 0.22);
    background: rgba(244, 63, 94, 0.08);
    color: var(--puc-red, #f43f5e);
}

.prod-guide {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
}

.prod-guide-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    max-width: 240px;
    padding: 18px 14px;
    border-radius: 12px;
    border: 1px dashed rgba(203, 213, 225, 0.94);
    background: rgba(248, 250, 252, 0.78);
    text-align: center;
}

.prod-guide-card i {
    font-size: 22px;
    color: var(--puc-slate-300, #cbd5e1);
}

.prod-guide-card strong {
    font-size: 13px;
    color: var(--puc-slate-700, #475569);
}

.prod-guide-hint {
    font-size: 11px;
    font-weight: 600;
    color: var(--puc-slate-500, #64748b);
    line-height: 1.45;
    max-width: 280px;
}

.prod-table-area {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.prod-table-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.96);
    background: rgba(255, 255, 255, 0.92);
    flex-shrink: 0;
}

.prod-table-copy {
    display: flex;
    flex-direction: column;
}

.prod-table-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
}

/* 상품 목록 .list-footer 와 유사 */
.prod-table-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border-top: 1px solid rgba(241, 245, 249, 0.94);
    background: rgba(248, 250, 252, 0.82);
    flex-shrink: 0;
}

.prod-table-footer-product {
    min-width: 0;
    max-width: 55%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.prod-table-footer-chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    flex-shrink: 0;
}

.prod-table-chip {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 3px 8px;
    border-radius: 999px;
    background: rgba(241, 245, 249, 0.94);
    color: var(--puc-slate-500, #64748b);
    font-size: 10px;
    font-weight: 700;
}

.prod-table-chip--warn {
    background: rgba(245, 158, 11, 0.1);
    color: var(--puc-amber, #f59e0b);
}

.prod-table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
}

.cell-date {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 36px;
}

.cell-date-txt {
    font-size: 12px;
    color: var(--puc-navy, #1b263b);
}

.cell-qty {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;
    min-height: 36px;
    padding: 0 10px;
    box-sizing: border-box;
    cursor: pointer;
    overflow: hidden;
}

.cell-qty--editing {
    cursor: text;
}

.cell-qty-val {
    font-size: 13px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cell-qty-u {
    font-size: 10px;
    color: var(--puc-slate-400, #94a3b8);
}

.cell-qty-inp {
    flex: 1;
    width: 100%;
    min-width: 0;
    border: none;
    background: transparent;
    padding: 6px 4px;
    text-align: right;
    font-size: 13px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
    outline: none;
    box-shadow: none;
}

.cell-acts {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.act-btn {
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--puc-slate-400, #94a3b8);
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.act-del:hover {
    background: rgba(244, 63, 94, 0.12);
    color: var(--puc-red, #f43f5e);
}

.tbl-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 28px 16px;
    color: var(--puc-slate-500, #64748b);
    text-align: center;
}

.tbl-empty i {
    font-size: 22px;
    color: var(--puc-slate-300, #cbd5e1);
}

.tbl-empty p {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
}

@media (max-width: 980px) {
    .prod-header,
    .prod-table-toolbar {
        flex-direction: column;
        align-items: flex-start;
    }

    .pf-controls,
    .pf-group,
    .pf-quick {
        flex-wrap: wrap;
    }

    .pf-group {
        min-width: 0;
    }
}
</style>

<style>
.prod-panel.prod-panel--vgt table.vgt-table {
    border: none;
    font-size: 12px;
}

.prod-panel.prod-panel--vgt table.vgt-table thead th.vth {
    background: #1b263b;
    color: #fff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    font-size: 10px;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0.02em;
    padding-top: 9px;
    padding-bottom: 9px;
}

.prod-panel.prod-panel--vgt table.vgt-table tbody td.vtd {
    padding: 0 10px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

.prod-panel.prod-panel--vgt table.vgt-table tbody td.vtd.vtd-qty-wrap {
    padding: 0;
}

.prod-panel.prod-panel--vgt table.vgt-table tbody tr:hover td.vtd {
    background: #f8fbff;
}

.prod-panel.prod-panel--vgt table.vgt-table tbody tr.row-new td.vtd {
    background: rgba(239, 246, 255, 0.28);
}

.prod-panel.prod-panel--vgt table.vgt-table tbody tr.row-mod td.vtd {
    background: rgba(245, 158, 11, 0.02);
}

/* 기준일 열: 항상 동일 폭의 왼쪽 테두리(투명)로 레이아웃 점프 방지, 변경 시 색만 전환 */
.prod-panel.prod-panel--vgt table.vgt-table tbody tr td.vtd.vtd-date-col {
    border-left: 3px solid transparent;
    box-sizing: border-box;
}

.prod-panel.prod-panel--vgt table.vgt-table tbody tr.row-new td.vtd.vtd-date-col {
    border-left-color: #007bff;
}

.prod-panel.prod-panel--vgt table.vgt-table tbody tr.row-mod td.vtd.vtd-date-col {
    border-left-color: rgba(245, 158, 11, 0.55);
}

.prod-panel.prod-panel--vgt table.vgt-table tbody tr.row-editing td.vtd {
    background: rgba(15, 23, 42, 0.015);
}

.prod-panel.prod-panel--vgt .vgt-wrap .vgt-wrap__footer {
    display: none;
}

.prod-panel.prod-panel--vgt .fixed-width-125 {
    width: 125px !important;
    min-width: 125px !important;
    max-width: 125px !important;
    box-sizing: border-box;
}

.prod-panel.prod-panel--vgt .fixed-width-qty-main {
    width: 160px !important;
    min-width: 160px !important;
    max-width: 160px !important;
    box-sizing: border-box;
}

.prod-panel.prod-panel--vgt .fixed-width-qty {
    width: 120px !important;
    min-width: 120px !important;
    max-width: 120px !important;
    box-sizing: border-box;
}

.prod-panel.prod-panel--vgt .fixed-width-act {
    width: 56px !important;
    min-width: 56px !important;
    max-width: 56px !important;
    box-sizing: border-box;
}

.prod-panel.prod-panel--vgt td.vtd-date-col .mx-datepicker {
    width: 100%;
    /* max-width: 125px; */
    box-sizing: border-box;
}

.prod-panel.prod-panel--vgt td.vtd-date-col .mx-input-wrapper {
    width: 100%;
}

.prod-panel.prod-panel--vgt td.vtd-date-col .cell-date {
    width: 100%;
    /* max-width: 125px; */
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
}

.prod-panel.prod-panel--vgt .cell-date-inp {
    width: 100%;
    /* max-width: 125px; */
    box-sizing: border-box;
    border: none;
    border-radius: 8px;
    padding: 5px 8px;
    font-size: 11px;
    color: #1b263b;
    background: #fff;
    outline: none;
    box-shadow: none;
    text-align: center;
}

.prod-panel.prod-panel--vgt .cell-date-inp:focus {
    border-color: rgba(37, 99, 235, 0.7);
}

.prod-panel.prod-panel--vgt .pf-date-input {
    width: 110px;
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 8px;
    padding: 6px 8px;
    font-size: 11px;
    color: #1b263b;
    background: #fff;
    outline: none;
    transition: border-color 0.18s ease;
}

.prod-panel.prod-panel--vgt .pf-date-input:focus {
    border-color: rgba(37, 99, 235, 0.68);
}
</style>
